// LICENSE_CODE ZON
'use strict';/*jslint ts:true, react:true*/
import React, {CSSProperties, Fragment, useContext,
  useEffect, useState} from 'react';
import cn from 'classnames';
import {Settings_context} from '../settings_ctx';
import {Editor} from '../../../shared/editor/editor';
import {Button} from '../lib/button';
import Arrow_img from '../img/homepage/how_it_works_arrow.svg';
import styles from './features.module.less';

interface i_feature_props {
  image: string;
  title: string;
  title_width?: string;
  description: string;
  description_size?: string;
  width?: string;
  width_unit?: string;
  image_wrapper_height?: string;
  image_wrapper_width?: string;
  image_wrapper_unit?: string;
  image_height?: string;
  image_width?: string;
  image_unit?: string;
}

interface i_features_props {
  title: string;
  title_size?: string;
  title_gap?: string;
  arrow_separator?: boolean;
  bg_color?: string;
  hidden_mobile?: boolean;
  max_width?: string;
  features: i_feature_props[];
  features_gap?: string;
}

const Feature = (props: i_feature_props)=>{
  const {image, title, description, image_wrapper_height, image_wrapper_width,
    image_wrapper_unit = 'px', image_height, image_width, title_width,
    image_unit = 'px', width, width_unit = 'px', description_size} = props;
  const {theme} = useContext(Settings_context);
  const img_dimensions = {
    '--img_width': image_width ? image_width+image_unit : '',
    '--img_height': image_height ? image_height+image_unit: '',
  } as CSSProperties;
  const img_wrapper_dimensions = {
    '--img_wrapper_width': image_wrapper_width ?
      image_wrapper_width+image_wrapper_unit : '',
    '--img_wrapper_height': image_wrapper_height ?
      image_wrapper_height+image_wrapper_unit : '',
  } as CSSProperties;
  const feature_width = {
    '--feature_width': width ? width+width_unit : '',
  } as CSSProperties;
  const t_width = {
    '--feature_title_width': title_width ? title_width : '100%',
  } as CSSProperties;
  const d_size = description_size ? description_size : 'p2';
  return <div className={styles.feature} style={feature_width}>
    <div className={styles.feature_image} style={img_wrapper_dimensions}>
      <img src={image} alt="image" className="lazyload"
        style={img_dimensions}/>
    </div>
    <p className={cn(theme.p1, styles.feature_title)} style={t_width}>
      {title}</p>
    <p className={cn(theme[d_size], theme.reg, styles.feature_description)}
      dangerouslySetInnerHTML={{__html: description}}></p>
  </div>;
};

export const Features = (props: i_features_props)=>{
  const {theme} = useContext(Settings_context);
  const bg_color = {
    '--features_bg_color': props.bg_color ? props.bg_color : '',
    '--container_width': props.max_width ? `${props.max_width}px` : '800px',
    '--features_gap': props.features_gap ? `${props.features_gap}px` : '64px',
  } as CSSProperties;
  const title_gap = {
    '--title_gap': props?.title_gap ? props.title_gap : '40px',
  } as CSSProperties;
  const title_size = props?.title_size ? props.title_size : 'H4';
  return <div className={cn(styles.features_container,
    props.hidden_mobile && styles.hidden)} style={bg_color}>
    <h4 className={cn(theme[title_size], styles.features_title)}
      style={title_gap}>{props.title}</h4>
    <div className={styles.features}>
      {Array.isArray(props.features) && props.features.map((f, i)=>
        <Fragment key={i}>
          <Feature {...f}/>
          {props.arrow_separator && i!==props.features.length-1 &&
            <div className={styles.arrow}><Arrow_img/></div>}
          </Fragment>)}
    </div>
  </div>;
};

interface i_features_editor_props {
  value: i_features_props;
  on_change: (new_value: i_features_props)=>void;
  on_cancel: ()=>void;
}

export const Features_editor = (props: i_features_editor_props)=>{
  const [state, set_state] = useState<i_features_props>(()=>({
    title: props.value?.title ?? '',
    title_size: props.value?.title_size ?? 'H4',
    title_gap: props.value?.title_gap ?? '',
    arrow_separator: props.value?.arrow_separator ?? false,
    bg_color: props.value?.bg_color ?? '',
    hidden_mobile: props.value?.hidden_mobile ?? false,
    max_width: props.value?.max_width ?? '800',
    features: props.value?.features ?? [],
    features_gap: props.value?.features_gap ?? '64',
  }));
  const features = state.features || [];
  const desc_label = 'Description (wrap words into <strong>word</strong>'
  +' if you need bold words';
  useEffect(()=>{
      set_state(c=>({...c, ...props.value}));
  }, [props.value]);
  return <Editor
    value={state}
    editors={[{key: 'title', label: 'Title', type: 'textarea'},
      {key: 'title_size', label: 'Title size', type: 'text'},
      {key: 'title_gap', label: 'Title gap', type: 'text'},
      {key: 'arrow_separator', label: 'Separator', type: 'boolean'},
      {key: 'hidden_mobile', label: 'Hidden in mobile', type: 'boolean'},
      {key: 'max_width', label: 'Container width', type: 'text'},
      {key: 'features_gap', label: 'Features gap', type: 'text'},
      {key: 'bg_color', label: 'Background color', type: 'color'}]}
    on_change={v=>set_state(v)}
    on_save={()=>props.on_change(state)}
    on_cancel={props.on_cancel}>
    <h4>Features: </h4>
    {features.map((li, i)=>
      <Fragment key={i}>
        <Editor
          title={li.title || `unknown`}
          key={i}
          value={li}
          editors={[
            {key: 'title', label: 'Title', type: 'textarea'},
            {key: 'title_width', label: 'Title width', type: 'text'},
            {key: 'description', label: desc_label, type: 'textarea'},
            {key: 'description_size', label: 'Desc size', type: 'text'},
            {key: 'image', label: 'Image', type: 'text'},
            {key: 'image_height', label: 'Image Height', type: 'text'},
            {key: 'image_width', label: 'Image Width', type: 'text'},
            {key: 'image_unit', label: 'Unit (px/rem/em)', type: 'text'},
            {key: 'image_wrapper_height', label: 'Image Wrapper Height',
              type: 'text'},
            {key: 'image_wrapper_width', label: 'Image Wrapper Width',
              type: 'text'},
            {key: 'image_wrapper_unit', label: 'Unit (px/rem/em)', type:
              'text'},
            {key: 'width', label: 'Image Wrapper Width',
              type: 'text'},
            {key: 'width_unit', label: 'Unit (px/rem/em)', type:
              'text'},
          ]}
          on_change={v=>{
            const items_new = [...features];
            items_new[i] = v;
            set_state({...state, features: items_new});
          }}>
        </Editor>
        <Button caption={'Delete feature'} size={'xs'}
          on_click={()=>{
            set_state({...state, features: features
              .filter((_, index)=>index!==i)});
          }}/>
      </Fragment>
    )}
    <Button caption={'Add feature'} size={'xs'}
      on_click={()=>{
        set_state({...state, features: [...features, {
          title: '',
          title_width: '',
          description: '',
          description_size: '',
          image: '',
          width: '',
          width_unit: '',
          image_width: '',
          image_height: '',
          image_unit: '',
          image_wrapper_height: '',
          image_wrapper_width: '',
          image_wrapper_unit: '',
        }]});
      }}/>
  </Editor>;
};
